// VHAL gRPC-Web client factory.
//
// Frontend → ctrl (gRPC-Web, auth) → emulator's vhal_proxy_server.
// ctrl re-exports the AOSP VehicleServer service verbatim; we use the
// generated client and attach Authorization + x-emulator-id headers on
// every call via interceptors.

import type { Client, Interceptor } from "@connectrpc/connect";
import { createClient } from "@connectrpc/connect";
import { createGrpcWebTransport } from "@connectrpc/connect-web";

import { VehicleServer } from "./gen/aosp/android/hardware/automotive/vehicle/VehicleServer_pb";

export interface VhalClientOpts {
  /**
   * Base URL of ctrl's gRPC-Web listener (e.g. "https://grpc.ctrl.dev.null.autos"
   * or "https://ctrl.dev.null.autos/grpc"). Trailing slash is OK.
   */
  baseUrl: string;
  /** ID of the emulator to target. Set on every request as x-emulator-id. */
  emulatorId: string;
  /**
   * Returns a current OIDC bearer token. Called once per RPC, so a stale
   * token from a cached refresh source is fine if the caller knows to
   * rotate it.
   */
  getToken: () => string | Promise<string>;
  /**
   * GitLab instance URL for ctrl's PAT authenticator (Null-Gitlab-Base
   * header). Omit when authenticating with OIDC (e.g. the portal).
   */
  getGitlabBase?: () => string | undefined;
}

/**
 * createVhalClient returns a typed Client<typeof VehicleServer> that calls
 * the ctrl gRPC-Web pass-through and forwards to the per-emulator vhal
 * proxy. The returned client is cheap to construct; recreate it when the
 * targeted emulator changes (or when the auth token source changes).
 */
export function createVhalClient(opts: VhalClientOpts): Client<typeof VehicleServer> {
  const auth: Interceptor = (next) => async (req) => {
    const token = await opts.getToken();
    if (token) {
      req.header.set("Authorization", `Bearer ${token}`);
    }
    req.header.set("x-emulator-id", opts.emulatorId);
    const gitlabBase = opts.getGitlabBase?.();
    if (gitlabBase) {
      req.header.set("Null-Gitlab-Base", gitlabBase);
    }
    return next(req);
  };

  const transport = createGrpcWebTransport({
    baseUrl: opts.baseUrl.replace(/\/+$/, ""),
    interceptors: [auth],
  });

  return createClient(VehicleServer, transport);
}

// Re-export the generated service + key message types so consumers can
// import everything they need from a single entry point.
export { VehicleServer } from "./gen/aosp/android/hardware/automotive/vehicle/VehicleServer_pb";
export {
  VehiclePropValueSchema,
  VehiclePropValuesSchema,
  type VehiclePropValue,
  type VehiclePropValues,
} from "./gen/aosp/android/hardware/automotive/vehicle/VehiclePropValue_pb";
export {
  VehiclePropValueRequestSchema,
  VehiclePropValueRequestsSchema,
  type VehiclePropValueRequest,
  type VehiclePropValueRequests,
  type SetValueResults,
  type GetValueResults,
} from "./gen/aosp/android/hardware/automotive/vehicle/VehiclePropValueRequest_pb";
export {
  VehiclePropertyStatus,
} from "./gen/aosp/android/hardware/automotive/vehicle/VehiclePropertyStatus_pb";
export {
  VehiclePropertyAccess,
} from "./gen/aosp/android/hardware/automotive/vehicle/VehiclePropertyAccess_pb";
export {
  VehiclePropertyChangeMode,
} from "./gen/aosp/android/hardware/automotive/vehicle/VehiclePropertyChangeMode_pb";
export {
  VehiclePropConfigSchema,
  type VehiclePropConfig,
} from "./gen/aosp/android/hardware/automotive/vehicle/VehiclePropConfig_pb";
export {
  VehicleAreaConfigSchema,
  type VehicleAreaConfig,
} from "./gen/aosp/android/hardware/automotive/vehicle/VehicleAreaConfig_pb";
export {
  StatusCode,
} from "./gen/aosp/android/hardware/automotive/vehicle/StatusCode_pb";
